import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

interface PaymentSettings {
  paymobEnabled: boolean;
  paymobStatus: string;
  lastUpdated?: string;
}

interface PaymentSettingsContextType {
  settings: PaymentSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const PaymentSettingsContext = createContext<PaymentSettingsContextType | undefined>(undefined);

interface PaymentSettingsProviderProps {
  children: ReactNode;
}

export const PaymentSettingsProvider: React.FC<PaymentSettingsProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [settings, setSettings] = useState<PaymentSettings>({
    paymobEnabled: false,
    paymobStatus: 'Disabled'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/settings/payment');
      setSettings(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load payment settings');
      // Set default settings on error
      setSettings({
        paymobEnabled: false,
        paymobStatus: 'Disabled'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    if (isAuthenticated) {
      await fetchSettings();
    }
  };

  useEffect(() => {
    // Only fetch settings when user is authenticated and auth loading is complete
    if (!authLoading && isAuthenticated) {
      fetchSettings();
    } else if (!authLoading && !isAuthenticated) {
      // If user is not authenticated, set loading to false and use default settings
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const value: PaymentSettingsContextType = {
    settings,
    loading,
    error,
    refreshSettings
  };

  return (
    <PaymentSettingsContext.Provider value={value}>
      {children}
    </PaymentSettingsContext.Provider>
  );
};

export const usePaymentSettings = (): PaymentSettingsContextType => {
  const context = useContext(PaymentSettingsContext);
  if (context === undefined) {
    throw new Error('usePaymentSettings must be used within a PaymentSettingsProvider');
  }
  return context;
};
